"use client";

/**
 * MiniFoilRow — 3-badge inline foil-grade preview for Scene 4 Day 0 block.
 *
 * Spec: research/landing-v2/09-wireframe-v2-part-b.md §6.2 block-1 variant
 *   "[sparkle A+ foil 48×48]  [B+ foil 48×48]  [A- foil 48×48]
 *    inline flex gap-3
 *    each = FoilGradeBadgeV2 size=\"sm\"
 *    static displays (no spring on entry — or tiny badge-entrance spring
 *    delayed by 200ms each)"
 *
 * Intentionally renders in place of a preview image — Day 0 is the reader
 * seeing three niches survive, not a Foundry artifact. Showing the foils
 * themselves (not a screenshot of the UI) keeps the voice-contract: this is
 * your result, not our product shot.
 *
 * Motion:
 *   - Each badge: spring-pop with 120ms stagger (matches §6.6 motion spec)
 *   - First (Top Pick) gets a small sparkle micro-chip above it
 *   - Reduced-motion: all badges render instantly at final state
 *
 * a11y:
 *   - Each FoilGradeBadge contributes its own aria-label ("Grade A+ ...")
 *   - Sparkle chip is decorative (aria-hidden)
 */

import { motion, useReducedMotion } from "framer-motion";
import { MagicStick3 } from "@solar-icons/react";
import { FoilGradeBadge as FoilGradeBadgeV2 } from "@/components/shared-primitives/FoilGradeBadge";
import { cn } from "@/lib/utils";

interface MiniFoilRowProps {
  /**
   * Array of letter grades to render left-to-right.
   * First entry is treated as the top pick (gets the sparkle chip).
   */
  grades: readonly string[];
  /** Extra classes on wrapper. */
  className?: string;
}

const BADGE_STAGGER_MS = 120;
const SPRING = { type: "spring" as const, stiffness: 220, damping: 14 };

export function MiniFoilRow({ grades, className }: MiniFoilRowProps) {
  const reduced = useReducedMotion();

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center gap-3 md:items-start",
        className,
      )}
    >
      <div className="flex items-end gap-3">
        {grades.map((grade, index) => {
          const isTopPick = index === 0;
          const delay = (BADGE_STAGGER_MS * index) / 1000;

          return (
            <motion.div
              key={`${grade}-${index}`}
              initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
              whileInView={
                reduced
                  ? { opacity: 1 }
                  : { opacity: 1, scale: 1 }
              }
              viewport={{ once: true, margin: "-60px" }}
              transition={
                reduced
                  ? { duration: 0.25, delay }
                  : { ...SPRING, delay }
              }
              className="relative flex flex-col items-center gap-2"
            >
              {isTopPick && (
                <span
                  aria-hidden
                  className={cn(
                    "inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em]",
                    "rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5",
                    "text-amber-300",
                  )}
                >
                  <MagicStick3 weight="BoldDuotone" size={10} aria-hidden />
                  Top pick
                </span>
              )}
              <FoilGradeBadgeV2
                grade={grade}
                size="xs"
                animateOnMount={false}
              />
            </motion.div>
          );
        })}
      </div>
      <span
        aria-hidden
        className="text-[12px] italic leading-[1.4] text-white/50"
      >
        Three niches. One top pick.
      </span>
    </div>
  );
}
